import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel } from '@material-ui/core';

import * as React from 'react';
import { IDocumentHtmlReplyJsonData } from '../../../services/useDocumentsApi';
import lang from '../../Relations/RelationsLang';
import HtmlContentStyled from '../../SensitiveData/HtmlContentStyled';
import { IAccessRelationData, IAccessRelationRawData } from '../AccessDomainModels';
import { HtmlContent, IIHtmlContentRef } from './HtmlContent';

export interface ISensitiveDataAddScopeDialogSettings {
	primaryTemplateName: string,
	secondaryTemplateName: string,
}

interface IProps {
	issueId: number,
	relations: (IAccessRelationRawData | IAccessRelationData)[],
	onAccept: (result: (IAccessRelationRawData | IAccessRelationData)[]) => void,
	onClose: () => void,
	settings: ISensitiveDataAddScopeDialogSettings,
}

type IResult = { [key: number]: IResultItem };

interface IResultItem {
	relation: (IAccessRelationRawData | IAccessRelationData),
	accepted: boolean,
}

export function SensitiveDataAddScopeDialog(props: IProps)
{
	const dialogContent = React.useRef<unknown>();
	const primaryContent = React.useRef<IIHtmlContentRef>();
	const secondaryContent = React.useRef<IIHtmlContentRef>();

	const [result, setResult] = React.useState<IResult>({});
	const [currentIndex, setCurrentIndex] = React.useState(0);
	
	const [accept, setAccept] = React.useState(false);
	const [isBottom, setIsBottom] = React.useState(false);

	const [isReadyPrimary, setIsReadyPrimary] = React.useState(false);
	const [isReadySecondary, setIsReadySecondary] = React.useState(false);
	const [htmlPrimary, setHtmlPrimary] = React.useState<IDocumentHtmlReplyJsonData>();
	const [htmlSecondary, setHtmlSecondary] = React.useState<IDocumentHtmlReplyJsonData>();

	const sensitiveRelations = props.relations.filter(x => x.role.sensitiveData);
	const currentRelation = sensitiveRelations[currentIndex];

	React.useEffect(() => {
		setAccept(false);
		setIsBottom(false);
		setIsReadyPrimary(false);
		setIsReadySecondary(false);
		setHtmlPrimary(null);
		setHtmlSecondary(null);
		setCurrentIndex(0);
	}, [props.relations])

	React.useEffect(() => {
		if (!(isReadyPrimary && isReadySecondary)) {
			return;
		}
		var element = dialogContent.current as HTMLDivElement;
		scrollLog("scrolInit", element);
		if (element.scrollHeight - element.scrollTop === element.clientHeight) {
			setIsBottom(true);
		}

	}, [isReadyPrimary, isReadySecondary])

	function handleScroll(ev: React.UIEvent<HTMLDivElement, UIEvent>) {
		if (!(isReadyPrimary && isReadySecondary)) {
			return;
		}

		var element = ev.target as HTMLDivElement;
		scrollLog("handleScroll", element);
		if (element.scrollHeight - element.scrollTop === element.clientHeight) {
			setIsBottom(true);
		}
	}

	function scrollLog(key, element) {
		console.log(key, {
				scrollHeight: element.scrollHeight,
				scrollTop: element.scrollTop,
				clientHeight: element.clientHeight
			},
			element.scrollHeight - element.scrollTop === element.clientHeight);
	}

	function handleAccept() {
		var primaryVariables = primaryContent.current.getVariables();
		var secondaryVariables = secondaryContent.current.getVariables();

		if (primaryVariables.isValid && secondaryVariables.isValid) {
			let variables = { ...primaryVariables.variables, ...secondaryVariables.variables };

			let newResult = { ...result };

			newResult[currentIndex] = {
				accepted: true,
				relation: {
					...currentRelation,
					documentVariables: variables,
					documentHtmlTemplateName: [props.settings.primaryTemplateName, props.settings.secondaryTemplateName].filter(x => !!x),
				},
			};

			nextOrClose(newResult);
		};
	}

	function handleReject() {
		let newResult = { ...result }
		newResult[currentIndex] = {
			accepted: false,
			relation: currentRelation,
		};

		nextOrClose(newResult);
	}

	function nextOrClose(newResult: IResult) {
		setResult(newResult);

		if (sensitiveRelations.length - 1 == currentIndex) {
			setAcceptData(newResult);
			return;
		}

		setAccept(false);
		setIsBottom(false);
		setIsReadyPrimary(false);
		setIsReadySecondary(false);
		setCurrentIndex(currentIndex + 1);
	}

	function setAcceptData(newResult: IResult) {
		let acceptedRelations = Object.values(newResult).filter(x => x.accepted).map(x => x.relation);
		props.onAccept(acceptedRelations);
	}

	function getAnyValue(key: keyof IDocumentHtmlReplyJsonData): any {
		if (htmlSecondary && htmlSecondary[key])
			return htmlSecondary[key];

		if (htmlPrimary && htmlPrimary[key])
			return htmlPrimary[key];

		return undefined;
	}

	function getHtmlValue(key: keyof IDocumentHtmlReplyJsonData): any {
		let v = getAnyValue(key) as string;
		if (!v) {
			return null;
		}
		if (typeof v !== "string")
			return null;

		return <span dangerouslySetInnerHTML={{ __html: v }} ></span>;
	}

	function isVisible(): boolean {
		return sensitiveRelations.length != 0
	}

	const canAccept = accept;
	const checkboxLabel = getHtmlValue("acceptanceCheckbox") ?? lang.acceptCheckboxLabel();
	let title = getHtmlValue("title");
	if (!title) {
		title = <span>{currentRelation?.resource.label}{", "}{currentRelation?.role.name}</span>
	}

	return <Dialog
		className="sensitive-data-add-scope-dialog"
		open={isVisible()}
		onClose={props.onClose}
		disableBackdropClick
		maxWidth={getAnyValue("maxWidthDialog")}
		fullScreen={getAnyValue("fullScreenDialog")}
		fullWidth={true}
	>
		<DialogTitle>
			<span>{lang.step()}{" "} {currentIndex + 1} {" "}{lang.of()}{" "} {sensitiveRelations.length}{": "}</span>
			{title}
		</DialogTitle>
		<DialogContent onScroll={handleScroll} ref={dialogContent}>
			{currentRelation && <HtmlContentStyled>
				<HtmlContent
					ref={primaryContent}
					templateName={props.settings.primaryTemplateName}
					relation={currentRelation}
					onChangeReady={(res, error) => {
						setIsReadyPrimary(true)
						setHtmlPrimary(res);
					}}
					issueId={props.issueId}
				/>
				{isReadyPrimary && isReadySecondary && <FormControlLabel
					control={
						<Checkbox
							checked={accept}
							onChange={(e) => setAccept(e.target.checked)}
							color="primary"
						/>
					}
					label={checkboxLabel}
				/>}
				<HtmlContent
					ref={secondaryContent}
					templateName={props.settings.secondaryTemplateName}
					relation={currentRelation}
					onChangeReady={(res, error) => {
						setIsReadySecondary(true);
						setHtmlSecondary(res);
					}}
					issueId={props.issueId}
				/>
			</HtmlContentStyled>}
		</DialogContent>
		<DialogActions>
			<Button onClick={props.onClose} color="default" variant="contained">
				{lang.get("cancel")}
			</Button>
			<Button onClick={handleReject} color="secondary" variant="contained">
				{lang.reject()}
			</Button>
			<Button onClick={handleAccept} color="primary" variant="contained" autoFocus disabled={!canAccept}>
				{lang.accept()}
			</Button>
		</DialogActions>
	</Dialog>
}